import { GridSize } from '@material-ui/core';
import { ChartOptions } from 'chart.js';
import { fill } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { TypedChartComponent } from 'react-chartjs-2/dist/types';
import trans from 'translation';
import { KButton, KColors, KContainer, KGrid, KLabel, KLoading } from 'uikit';
import { v4 as uuidv4 } from 'uuid';
interface IProps extends Omit<TypedChartComponent<'doughnut'>, 'data'> {
  data: any;
  colors?: string[];
  mappedLabels?: Record<string, string>;
  mappedFunctions?: Record<string, any>;
  cols?: number;
  isLoading?: boolean;
  title?: string;
}

const COLORS = [
  '#13A89E',
  '#FFA000',
  '#4B6FD9',
  '#FFA000',
  '#994BD9',
  '#20BFF0',
  '#C00505',
  '#C0C0C0',
  '#5ac8fa'
];

const DEFAULT_SIZE = 110;

const DoughnutWithLegends = ({
  data,
  mappedLabels,
  colors = COLORS,
  mappedFunctions = [],
  cols = 1,
  isLoading = false,
  title,
  ...rest
}: IProps) => {
  const id = useMemo(() => uuidv4(), []);
  const plugins = useMemo(
    () => [
      {
        id: 'centerText',
        beforeDraw: (chart: any) => {
          const { width, height, ctx } = chart;
          ctx.restore();
          const fontSize = (height / 60).toFixed(2);
          ctx.font = `bold ${fontSize}em sans-serif`;
          ctx.textBaseline = 'middle';

          const total = chart.data.datasets[0].data.reduce(
            (sum: number, value: number) => sum + Number(value),
            0
          );
          const text = total;
          const textX = Math.round((width - ctx.measureText(text).width) / 2);
          const textY = height / 2;

          ctx.fillStyle = '#000';
          ctx.fillText(text, textX, textY);
          ctx.save();
        }
      },
      {
        id: 'emptyDoughnut',
        afterDraw(chart: any, args: any, options: any) {
          const { datasets } = chart.data;
          const { color, width, radiusDecrease } = options;
          let hasData = datasets.some((dataset: any) =>
            dataset.data.some((value: any) => value > 0)
          );

          if (!hasData) {
            const {
              chartArea: { left, top, right, bottom },
              ctx
            } = chart;
            const centerX = (left + right) / 2;
            const centerY = (top + bottom) / 2;
            const r = Math.min(right - left, bottom - top) / 2;

            ctx.beginPath();
            ctx.lineWidth = width || 2;
            ctx.strokeStyle = color || 'rgba(255, 128, 0, 0.5)';
            ctx.arc(
              centerX,
              centerY,
              Math.abs(r - radiusDecrease) || 0,
              0,
              2 * Math.PI
            );
            ctx.stroke();
          }
        }
      }
    ],
    []
  );

  const _data = useMemo(() => {
    const defaultData = Array(3);

    const labels = data
      ? Object.keys(data).map(k => mappedLabels?.[k] ?? k)
      : fill([...defaultData], trans('unknown'));
    const values = data ? Object.values(data) : fill(defaultData, 0);
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderWidth: 0
        }
      ]
    };
  }, [colors, data, mappedLabels]);

  const options: ChartOptions<'doughnut'> = useMemo(
    () => ({
      cutout: '70%',
      borderColor: '#0e5421',
      plugins: {
        legend: {
          display: false,
          position: 'right',
          labels: {
            color: '#0e5421'
          }
        },
        tooltip: {
          enabled: true,
          callbacks: {
            title: tooltipItems => {
              return trans(tooltipItems[0].label); // Customize title
            }
          }
        },
        emptyDoughnut: {
          color: KColors.border.light,
          width: 20,
          radiusDecrease: 10
        }
      }
    }),
    []
  );

  const renderLegends = useCallback(() => {
    const { labels } = _data;
    // const matrix = chunk(labels, cols);
    const itemSize = (12 / cols) as GridSize;
    return (
      <KGrid.Container spacing={3} style={{ width: '100%' }}>
        {labels?.map((legend, idx) => {
          return (
            <KGrid.Item xs={itemSize} key={`legend-${id}-${idx}`}>
              <KContainer.View
                dp="flex"
                row
                alignItems="center"
                justifyContent="space-between"
              >
                <KContainer.View
                  dp="flex"
                  row
                  alignItems="center"
                  gap={'0.25rem'}
                >
                  <KContainer.View
                    background={_data?.datasets?.[0]?.backgroundColor?.[idx]}
                    height={12}
                    width={12}
                  />
                  <KLabel.Text>{trans(legend)}</KLabel.Text>
                  {mappedFunctions?.[legend] && (
                    <KButton.Icon
                      icon="HelpOutline"
                      size="sm"
                      onPress={mappedFunctions?.[legend]}
                    />
                  )}
                </KContainer.View>
                <KContainer.View>
                  <KLabel.Text textAlign="right" typo="TextNmMedium">
                    {_data?.datasets?.[0]?.data[idx]?.toString()}
                  </KLabel.Text>
                </KContainer.View>
              </KContainer.View>
            </KGrid.Item>
          );
        })}
      </KGrid.Container>
    );
  }, [_data, cols, id, mappedFunctions]);
  return (
    <>
      {isLoading && <KLoading />}
      <KContainer.View
        dp="flex"
        row
        justifyContent="flex-start"
        alignItems="flex-end"
        gap={'1.75rem'}
      >
        <KContainer.View dp="flex">
          {title && (
            <KLabel.Text
              typo="TextMdMedium"
              minW={`${DEFAULT_SIZE}px`}
              textAlign="center"
              marginB={'0.5rem'}
            >
              {title}
            </KLabel.Text>
          )}
          <KContainer.View>
            <Doughnut
              style={{ width: DEFAULT_SIZE, height: DEFAULT_SIZE }}
              data={_data}
              options={options}
              plugins={plugins}
              {...rest}
            />
          </KContainer.View>
        </KContainer.View>
        <KContainer.View
          dp="flex"
          flexG={1}
          minH={DEFAULT_SIZE}
          row
          alignItems="center"
          justifyContent="flex-start"
        >
          {renderLegends()}
        </KContainer.View>
      </KContainer.View>
    </>
  );
};

export default memo(DoughnutWithLegends);
